import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Collapse, IconButton, Divider, Avatar, Button } from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import AddIcon from '@mui/icons-material/Add';
import { stringToColor } from '../../../shared/commonFunctions';
import SafeCommentRenderer from '../../../shared/SafeCommentRenderer';
import { prepareContentForEditing, decodeHtmlContent } from '../../../shared/htmlUtils';
import CommentBox from '../../dashboard/CommentBox';

const getInitials = (name) => {
  if (!name) return 'U';
  return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
};

const getNoteTime = (note) => {
  if (!note || !note.created_at) return '';
  try {
    const date = new Date(note.created_at);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
  } catch (e) {
    return '';
  }
};

const getNoteContent = (note) => {
  if (!note) return '';
  let content = '';
  if (typeof note === 'string') content = note;
  else if (typeof note === 'object') {
    content = note.note || note.notes || note.content || note.comment || '';
  } else {
    content = String(note);
  }
  return decodeHtmlContent(prepareContentForEditing(content));
};

const isActivationKey = (event) => event.key === 'Enter' || event.key === ' ';

const AssigneeNoteGroup = ({ groupName, notes, isEditable, onEditNote, onDeleteNote, hideAvatar, t, showCount = false }) => {
  const [editingNoteIdx, setEditingNoteIdx] = useState(null);
  const [expanded, setExpanded] = useState(true);

  const handleSaveEdit = (note, content, idx) => {
    if (onEditNote) {
      onEditNote(note, content);
    }
    setEditingNoteIdx(null);
  };

  return (
    <Box className="assignee-group-container">
      <Box 
        className="assignee-group-header"
        onClick={() => setExpanded(!expanded)}
        onKeyDown={(event) => {
          if (isActivationKey(event)) {
            event.preventDefault();
            setExpanded(!expanded);
          }
        }}
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
      >
        <Box className="assignee-group-header-left">
          {!hideAvatar && (
            <Avatar 
              className="assignee-avatar"
              sx={{ width: 28, height: 28, bgcolor: stringToColor(groupName) }}
            >
              {getInitials(groupName)}
            </Avatar>
          )}
          <Typography className="assignee-name">
            {groupName}
          </Typography>
        </Box>
        <Box className="assignee-group-header-right" display="flex" alignItems="center" gap={1}>
          {showCount && (
            <span className="user-item-count-badge">
              {notes.length} {notes.length === 1 ? 'Item' : 'Items'}
            </span>
          )}
          <IconButton size="small" className="expand-btn">
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>
      </Box>
      <Collapse in={expanded}>
        <Box className="assignee-group-content">
          {notes.map((note, idx) => (
            <Box key={note.id || note.note_id || idx} className="note-item">
              {editingNoteIdx === idx ? (
                 <Box className="note-edit-container">
                   <CommentBox 
                     initialContent={getNoteContent(note)}
                     onSubmit={(content) => handleSaveEdit(note, content, idx)}
                     onCancel={() => setEditingNoteIdx(null)}
                   />
                 </Box>
              ) : (
                 <>
                    <Box className="comment-content">
                      <SafeCommentRenderer content={getNoteContent(note)} />
                    </Box>
                   {note?.created_at && (
                     <Box className="note-timestamp-container">
                       <Typography className="note-timestamp">
                         {getNoteTime(note)}
                       </Typography>
                     </Box>
                   )}
                   {isEditable && (
                     <Box className="note-actions">
                       <Button 
                         size="small" 
                         variant="outlined"
                         className="action-btn edit-btn"
                         startIcon={<EditOutlinedIcon />}
                         onClick={() => setEditingNoteIdx(idx)}
                       >
                         {t('common.edit')}
                       </Button>
                       <Button 
                         size="small" 
                         variant="outlined"
                         className="action-btn delete-btn"
                         startIcon={<DeleteOutlineOutlinedIcon />}
                         onClick={() => { if(onDeleteNote) onDeleteNote(note); }}
                       >
                         {t('common.delete')}
                       </Button>
                     </Box>
                   )}
                 </>
              )}
            </Box>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

const normalizeGroupedNotes = (notes, t, groupByProject) => {
  if (!Array.isArray(notes) || notes.length === 0) return [];

  const hasProjectGroups = notes.some(
    (item) => item && (item.project_id || item.project_name) && Array.isArray(item.notes)
  );
  const hasNestedUserGroups = notes.some(
    (item) => item && Array.isArray(item.notes) && (item.user_id || item.name || item.owner)
  );

  if (hasProjectGroups) {
    return notes.map((group) => ({
      groupName: group?.project_name || t('common.general'),
      hideAvatar: true,
      notes: Array.isArray(group?.notes) ? group.notes : [],
    }));
  }

  if (hasNestedUserGroups) {
    return notes.map((group) => ({
      groupName: group?.name || group?.owner || (groupByProject ? t('common.general') : t('common.unassigned')),
      hideAvatar: false,
      notes: Array.isArray(group?.notes) ? group.notes : [group].filter(Boolean),
    }));
  }

  const grouped = notes.reduce((acc, note) => {
    const key = groupByProject
      ? (note?.project_name || t('common.general'))
      : (note?.name || note?.owner || t('common.unassigned'));

    if (!acc[key]) {
      acc[key] = {
        groupName: key,
        hideAvatar: groupByProject,
        notes: [],
      };
    }

    acc[key].notes.push(note);
    return acc;
  }, {});

  return Object.values(grouped);
};

const AdditionalNotes = ({ notes = [], isEditable = false, onAddNote, onEditNote, onDeleteNote, groupByProject = false, disableAddNote = false, showCount = false }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [isAdding, setIsAdding] = useState(false);

  const groupedNotes = normalizeGroupedNotes(notes, t, groupByProject);

  const handleAddSubmit = (content) => {
    if (onAddNote) {
      onAddNote(content);
    }
    setIsAdding(false);
  };

  return (
    <Box className="additional-notes-container">
      <Box 
        className="notes-header"
        onClick={() => setExpanded(!expanded)}
        onKeyDown={(event) => {
          if (isActivationKey(event)) {
            event.preventDefault();
            setExpanded(!expanded);
          }
        }}
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
      >
        <Typography className="section-title">
          {t('dailyStandup.additionalNotes.title')}
        </Typography>
        <Box className="notes-header-right" display="flex" alignItems="center" gap={2}>
          {isEditable && !isAdding && !disableAddNote && (
            <Button 
              startIcon={<AddIcon />} 
              variant="outlined" 
              size="small" 
              className="action-btn add-btn"
              onClick={(e) => {
                e.stopPropagation();
                setIsAdding(true);
                if (!expanded) setExpanded(true);
              }}
            >
              {t('common.addNote')}
            </Button>
          )}
          <IconButton size="small" className="header-icon-btn" onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}>
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>
      </Box>
      <Collapse in={expanded}>
        <Box className="notes-divider-container">
          <Divider />
        </Box>
        <Box className="notes-content">
          {isAdding && (
            <Box className="add-note-wrapper">
               <CommentBox 
                 initialContent=""
                 hideAttachments={true}
                 onSubmit={handleAddSubmit}
                 onCancel={() => setIsAdding(false)}
                 autoFocusEditor={true}
               />
            </Box>
          )}
          
          {groupedNotes.length === 0 && !isAdding ? (
            <Box className="empty-card-state">
              <InfoOutlinedIcon className="empty-icon" />
              <Typography className="empty-text">
                {t('dailyStandup.additionalNotes.noNotes')}
              </Typography>
            </Box>
          ) : (
            <Box className="assignee-cards-container">
              {groupedNotes.map(({ groupName, notes: groupNotes, hideAvatar }) => (
                <AssigneeNoteGroup 
                  key={groupName} 
                  groupName={groupName} 
                  notes={groupNotes} 
                  isEditable={isEditable}
                  onEditNote={onEditNote}
                  onDeleteNote={onDeleteNote}
                  hideAvatar={hideAvatar}
                  t={t}
                  showCount={showCount}
                />
              ))}
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default AdditionalNotes;
